/**
 * AutoGlow · Security Center React Query hooks · Sprint T-15.
 *
 * 1 endpoint = 1 hook. NO derived state. NO calculations.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { securityApi } from "../services/securityApi";

const FIVE_MIN = 5 * 60 * 1000;
const ONE_MIN = 60 * 1000;

const baseOpts = {
  staleTime: ONE_MIN,
  gcTime: FIVE_MIN,
  retry: 1,
  refetchOnWindowFocus: false,
};

// ---------------------------------------------------------------------------
// 2FA
// ---------------------------------------------------------------------------

export const useTfaStatus = () =>
  useQuery({
    queryKey: ["security.2fa.status"],
    queryFn: () => securityApi.twofa.status().then((r) => r.data),
    ...baseOpts,
  });

export const useTfaEnable = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => securityApi.twofa.enable().then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["security.2fa.status"] }),
  });
};

export const useTfaEnableVerify = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code) => securityApi.twofa.enableVerify(code).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["security.2fa.status"] }),
  });
};

export const useTfaDisable = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code) => securityApi.twofa.disable(code).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["security.2fa.status"] }),
  });
};

export const useTfaRegenerateBackup = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code) => securityApi.twofa.regenerateBackup(code).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["security.2fa.status"] }),
  });
};

// ---------------------------------------------------------------------------
// API Keys
// ---------------------------------------------------------------------------

export const useApiKeyScopes = () =>
  useQuery({
    queryKey: ["security.apikeys.scopes"],
    queryFn: () => securityApi.apiKeys.scopes().then((r) => r.data),
    staleTime: FIVE_MIN,
    ...baseOpts,
  });

export const useApiKeysList = () =>
  useQuery({
    queryKey: ["security.apikeys.list"],
    queryFn: () => securityApi.apiKeys.list().then((r) => r.data),
    ...baseOpts,
  });

export const useApiKeyCreate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => securityApi.apiKeys.create(payload).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["security.apikeys.list"] }),
  });
};

export const useApiKeyRevoke = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => securityApi.apiKeys.revoke(id).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["security.apikeys.list"] }),
  });
};

export const useApiKeyRotate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => securityApi.apiKeys.rotate(id).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["security.apikeys.list"] }),
  });
};

// ---------------------------------------------------------------------------
// Compliance
// ---------------------------------------------------------------------------

export const useCompliancePolicies = () =>
  useQuery({
    queryKey: ["security.compliance.policies"],
    queryFn: () => securityApi.compliance.policies().then((r) => r.data),
    staleTime: FIVE_MIN,
    ...baseOpts,
  });

export const useExportMe = () =>
  useMutation({
    mutationFn: () => securityApi.compliance.exportMe().then((r) => r.data),
  });

export const useExportOther = () =>
  useMutation({
    mutationFn: (params) => securityApi.compliance.exportOther(params).then((r) => r.data),
  });

export const useDeleteMe = () =>
  useMutation({
    mutationFn: () => securityApi.compliance.deleteMe().then((r) => r.data),
  });

export const useDeleteUser = () =>
  useMutation({
    mutationFn: ({ user_id, mode = "soft" }) =>
      securityApi.compliance.deleteUser(user_id, mode).then((r) => r.data),
  });

export const useDeleteCustomer = () =>
  useMutation({
    mutationFn: ({ customer_id, mode = "soft" }) =>
      securityApi.compliance.deleteCustomer(customer_id, mode).then((r) => r.data),
  });

// ---------------------------------------------------------------------------
// Audit events
// ---------------------------------------------------------------------------

export const useAuditEvents = (filters = {}, enabled = true) =>
  useQuery({
    queryKey: ["security.audit.list", filters],
    queryFn: () => securityApi.audit.list(filters).then((r) => r.data),
    enabled,
    ...baseOpts,
  });

// ---------------------------------------------------------------------------
// Composite hooks (Overview)
// ---------------------------------------------------------------------------

export const useSecurityOverview = () => {
  // Each block is fetched independently · the Overview composes the cards.
  const tfa = useTfaStatus();
  const apikeys = useApiKeysList();
  const lastLogin = useAuditEvents({ limit: 1, action: "auth.login.ok" });
  const failuresRecent = useAuditEvents({ limit: 10, only_failures: true });
  const rateLimits = useAuditEvents({ limit: 10, action: "ratelimit.triggered" });
  const recent = useAuditEvents({ limit: 5 });
  return { tfa, apikeys, lastLogin, failuresRecent, rateLimits, recent };
};
